import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  CircularProgress,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip
} from '@material-ui/core'
import {
  Close as CloseIcon,
  SelectAll as SelectAllIcon,
  PlayArrow as PlayArrowIcon,
  Landscape as LandscapeIcon,
  Opacity as OpacityIcon,
  WbSunny as LightingIcon,
  Nature as NatureIcon,
  Layers as LayersIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  EmojiObjects as LightbulbIcon
} from '@material-ui/icons'
import VisualReviewModal from './VisualReviewModal'
import ExtractionRulesDrawer from './ExtractionRulesDrawer'

// Discipline → color + icon mapping
const DISCIPLINE_CONFIG = {
  landscape: { color: '#2e7d32', bg: '#e8f5e9', label: 'Landscape', icon: NatureIcon },
  irrigation: { color: '#1565c0', bg: '#e3f2fd', label: 'Irrigation', icon: OpacityIcon },
  lighting: { color: '#f57f17', bg: '#fff8e1', label: 'Lighting', icon: LightingIcon },
  hardscape: { color: '#6d4c41', bg: '#efebe9', label: 'Hardscape', icon: LayersIcon },
  grading: { color: '#757575', bg: '#f5f5f5', label: 'Grading', icon: LayersIcon },
  demolition: { color: '#c62828', bg: '#ffebee', label: 'Demolition', icon: LayersIcon },
  cover: { color: '#546e7a', bg: '#eceff1', label: 'Cover Sheet', icon: AssignmentIcon },
  notes: { color: '#546e7a', bg: '#eceff1', label: 'Notes', icon: AssignmentIcon },
  detail: { color: '#7b1fa2', bg: '#f3e5f5', label: 'Detail', icon: LayersIcon },
  tree_inventory: { color: '#33691e', bg: '#f1f8e9', label: 'Tree Inventory', icon: NatureIcon },
  other: { color: '#9e9e9e', bg: '#fafafa', label: 'Other', icon: AssignmentIcon }
}

const getDisciplineConfig = (discipline) =>
  DISCIPLINE_CONFIG[discipline] || DISCIPLINE_CONFIG.other

const PlanPageSelector = ({ open, onClose, doc, projectId, url, onParsed }) => {
  const parseResult = doc && doc.parseResult
  const sheetIndex = (parseResult && Array.isArray(parseResult.sheetIndex)) ? parseResult.sheetIndex : []
  const titleBlock = parseResult && parseResult.titleBlock

  const [selected, setSelected] = React.useState(() => {
    // Pre-select pages that have schedules
    const initial = new Set()
    sheetIndex.forEach(s => {
      if (s.hasSchedule) initial.add(s.page)
    })
    return initial
  })
  const [parsing, setParsing] = React.useState(false)
  const [error, setError] = React.useState('')
  const [showVisualReview, setShowVisualReview] = React.useState(false)
  const [rulesDrawer, setRulesDrawer] = React.useState({ open: false })

  // Reset selection when sheetIndex changes
  React.useEffect(() => {
    const initial = new Set()
    sheetIndex.forEach(s => {
      if (s.hasSchedule) initial.add(s.page)
    })
    setSelected(initial)
  }, [parseResult])

  const togglePage = (page) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(page)) next.delete(page)
      else next.add(page)
      return next
    })
  }

  const selectAllSchedules = () => {
    const sched = new Set()
    sheetIndex.forEach(s => {
      if (s.hasSchedule) sched.add(s.page)
    })
    setSelected(sched)
  }

  const selectAll = () => {
    setSelected(new Set(sheetIndex.map(s => s.page)))
  }

  const selectNone = () => {
    setSelected(new Set())
  }

  const handleParse = () => {
    if (selected.size === 0) return
    setParsing(true)
    setError('')

    const pages = Array.from(selected).sort((a, b) => a - b)
    axios.post(
      `${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}/parse-pages`,
      { pages }
    ).then(res => {
      setParsing(false)
      if (onParsed) onParsed(res.data)
      onClose()
    }).catch(err => {
      setParsing(false)
      setError(err.response?.data?.error || 'Failed to parse selected pages')
    })
  }

  const scheduleCount = sheetIndex.filter(s => s.hasSchedule).length
  const classifyTime = parseResult && parseResult.classifyTime

  // Group sheets by discipline for summary
  const disciplineCounts = {}
  sheetIndex.forEach(s => {
    const d = s.discipline || 'other'
    disciplineCounts[d] = (disciplineCounts[d] || 0) + 1
  })

  return (
    <>
    <Dialog open={open} onClose={parsing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Typography variant="h6" style={{ lineHeight: 1.2 }}>
              Plan Set Classification
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {sheetIndex.length} pages classified
              {classifyTime ? ` in ${classifyTime}s` : ''}
              {' — '}{scheduleCount} page{scheduleCount !== 1 ? 's' : ''} with extractable schedules
            </Typography>
          </div>
          <IconButton size="small" onClick={onClose} disabled={parsing}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {/* Title Block summary */}
        {titleBlock && (titleBlock.projectName || titleBlock.date) && (
          <div style={{
            marginBottom: 16,
            padding: '10px 14px',
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap'
          }}>
            {titleBlock.projectName && (
              <div>
                <Typography variant="caption" color="textSecondary">Project</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>{titleBlock.projectName}</Typography>
              </div>
            )}
            {titleBlock.date && (
              <div>
                <Typography variant="caption" color="textSecondary">Date</Typography>
                <Typography variant="body2">{titleBlock.date}</Typography>
              </div>
            )}
            {titleBlock.architect && (
              <div>
                <Typography variant="caption" color="textSecondary">Architect</Typography>
                <Typography variant="body2">{titleBlock.architect}</Typography>
              </div>
            )}
          </div>
        )}

        {/* Discipline summary chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {Object.entries(disciplineCounts).map(([disc, count]) => {
            const cfg = getDisciplineConfig(disc)
            return (
              <Chip
                key={disc}
                size="small"
                label={`${cfg.label} (${count})`}
                style={{
                  backgroundColor: cfg.bg,
                  color: cfg.color,
                  fontWeight: 500,
                  fontSize: 12,
                  height: 24
                }}
              />
            )
          })}
        </div>

        {/* Selection controls */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center'
        }}>
          <Button size="small" onClick={selectAllSchedules} style={{ textTransform: 'none', fontSize: 12 }}>
            Select Schedules ({scheduleCount})
          </Button>
          <Button size="small" onClick={selectAll} style={{ textTransform: 'none', fontSize: 12 }}>
            Select All
          </Button>
          <Button size="small" onClick={selectNone} style={{ textTransform: 'none', fontSize: 12 }}>
            Clear
          </Button>
          {doc && doc.fileType === 'pdf' && sheetIndex.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<VisibilityIcon style={{ fontSize: 16 }} />}
              onClick={() => setShowVisualReview(true)}
              disabled={parsing}
              style={{ textTransform: 'none', fontSize: 12, marginLeft: 4 }}
            >
              Visual Review
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Typography variant="caption" color="textSecondary">
            {selected.size} selected
          </Typography>
        </div>

        {/* Page list */}
        <div style={{
          maxHeight: 400,
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: 4
        }}>
          {sheetIndex.map((sheet, idx) => {
            const cfg = getDisciplineConfig(sheet.discipline)
            const isSelected = selected.has(sheet.page)
            const Icon = cfg.icon

            return (
              <div
                key={sheet.page}
                onClick={() => !parsing && togglePage(sheet.page)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  cursor: parsing ? 'default' : 'pointer',
                  backgroundColor: isSelected ? '#e3f2fd' : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                  borderBottom: idx < sheetIndex.length - 1 ? '1px solid #f0f0f0' : 'none',
                  transition: 'background-color 0.1s'
                }}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={parsing}
                  size="small"
                  color="primary"
                  style={{ padding: 2 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => togglePage(sheet.page)}
                />

                <Typography variant="caption" style={{
                  width: 36,
                  textAlign: 'right',
                  color: '#9e9e9e',
                  fontFamily: 'monospace'
                }}>
                  p{sheet.page}
                </Typography>

                <Icon style={{ fontSize: 18, color: cfg.color }} />

                <Chip
                  size="small"
                  label={cfg.label}
                  style={{
                    backgroundColor: cfg.bg,
                    color: cfg.color,
                    height: 20,
                    fontSize: 11,
                    fontWeight: 500,
                    minWidth: 80
                  }}
                />

                {sheet.sheetNumber && (
                  <Typography variant="body2" style={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    fontSize: 13,
                    minWidth: 50
                  }}>
                    {sheet.sheetNumber}
                  </Typography>
                )}

                <Typography variant="body2" style={{ flex: 1, fontSize: 13 }}>
                  {sheet.title}
                </Typography>

                {sheet.hasSchedule && (
                  <Chip
                    size="small"
                    label="Has Schedule"
                    style={{
                      backgroundColor: '#fff8e1',
                      color: '#f57f17',
                      height: 18,
                      fontSize: 10,
                      fontWeight: 600
                    }}
                  />
                )}
                {sheet.hasSchedule && (
                  <Tooltip title="Improve Extraction">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setRulesDrawer({
                          open: true,
                          page: sheet.page,
                          discipline: sheet.discipline
                        })
                      }}
                      style={{ padding: 2 }}
                    >
                      <LightbulbIcon style={{ fontSize: 16, color: '#f57f17' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )
          })}
        </div>

        {/* Parsing progress */}
        {parsing && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="textSecondary">
                Extracting schedules from {selected.size} page{selected.size !== 1 ? 's' : ''}…
              </Typography>
            </div>
            <LinearProgress />
          </div>
        )}

        {error && (
          <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={parsing}>
          Cancel
        </Button>
        <Button
          onClick={handleParse}
          variant="contained"
          color="primary"
          disabled={parsing || selected.size === 0}
          startIcon={parsing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
          style={{ textTransform: 'none' }}
        >
          {parsing ? 'Extracting…' : `Extract ${selected.size} Page${selected.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>

    </Dialog>

    {showVisualReview && (
      <VisualReviewModal
        open={showVisualReview}
        onClose={(updatedSelection) => {
          setShowVisualReview(false)
          if (updatedSelection) setSelected(updatedSelection)
        }}
        sheetIndex={sheetIndex}
        selected={selected}
        projectId={projectId}
        docId={doc && doc._id}
        url={url}
      />
    )}

    <ExtractionRulesDrawer
      open={rulesDrawer.open}
      onClose={() => setRulesDrawer({ open: false })}
      url={url}
      mode="builder"
      projectId={projectId}
      docId={doc && doc._id}
      page={rulesDrawer.page}
      discipline={rulesDrawer.discipline}
    />
    </>
  )
}

export default PlanPageSelector
